import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FolderTree, RefreshCw, Loader2 } from "lucide-react";
import { Section } from "./Section";
import { OuSelect, OuTreeMulti, useOuTree } from "@/components/OuPicker";

export function OuSettingsSection({ s, setS }) {
  const { t } = useLang();
  const { tree, count, discovery, loading } = useOuTree();
  const [syncing, setSyncing] = useState(false);
  const ou = s.onPremOu || {};
  const setOu = (patch) => setS({ ...s, onPremOu: { ...ou, ...patch } });
  const cs = s.cloudSync || {};
  const setCs = (patch) => setS({ ...s, cloudSync: { ...cs, ...patch } });
  const cloudSyncOn = !!s.cloudSync?.enabled;

  const triggerDelta = async () => {
    setSyncing(true);
    try { await api.post("/agent/entra-delta-sync"); toast.success(t("ou.deltaQueued")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("ou.deltaFail")); }
    finally { setSyncing(false); }
  };

  return (
    <Section icon={FolderTree} title={t("ou.title")}>
      <p className="mb-4 text-xs text-zinc-400">{t("ou.intro")}</p>

      <div className="mb-4 flex items-center gap-2 text-xs text-zinc-500" data-testid="ou-discovery-status">
        <span className={`h-2 w-2 rounded-full ${count > 0 ? "bg-emerald-500" : "bg-zinc-300"}`} />
        {loading ? t("ou.loading") : count > 0
          ? `${count} ${t("ou.discovered")}${discovery?.receivedAt ? ` · ${new Date(discovery.receivedAt).toLocaleString()}` : ""}`
          : t("ou.none")}
      </div>

      <div className="space-y-5">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ou.provisioningUsers")}</Label>
          <p className="mb-1.5 text-[11px] text-zinc-400">{t("ou.provisioningUsersHelp")}</p>
          <OuSelect value={ou.defaultProvisioningOuDn || ""} onChange={(v) => setOu({ defaultProvisioningOuDn: v })}
            tree={tree} count={count} testid="ou-provisioning-users" placeholder={t("ou.pick")} />
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ou.provisioningGroups")}</Label>
          <p className="mb-1.5 text-[11px] text-zinc-400">{t("ou.provisioningGroupsHelp")}</p>
          <OuSelect value={ou.defaultGroupOuDn || ""} onChange={(v) => setOu({ defaultGroupOuDn: v })}
            tree={tree} count={count} testid="ou-provisioning-groups" placeholder={t("ou.pick")} />
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ou.deprovisioning")}</Label>
          <p className="mb-1.5 text-[11px] text-zinc-400">{t("ou.deprovisioningHelp")}</p>
          <OuSelect value={ou.deprovisioningOuDn || ""} onChange={(v) => setOu({ deprovisioningOuDn: v })}
            tree={tree} count={count} testid="ou-deprovisioning" placeholder={t("ou.pick")} />
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ou.reconciliation")}</Label>
          <p className="mb-1.5 text-[11px] text-zinc-400">{t("ou.reconciliationHelp")}</p>
          <OuTreeMulti value={ou.reconciliationOuDns || []} onChange={(v) => setOu({ reconciliationOuDns: v })}
            tree={tree} count={count} testid="ou-reconciliation" />
        </div>

        <label className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <div className="pr-6">
            <div className="text-sm font-bold text-zinc-900">{t("ou.autoDelta")}</div>
            <div className="text-sm text-zinc-500">{t("ou.autoDeltaHelp")}</div>
          </div>
          <Switch checked={ou.autoDeltaSyncOnCreate !== false && !cloudSyncOn} disabled={cloudSyncOn} data-testid="ou-auto-delta"
            onCheckedChange={(v) => setOu({ autoDeltaSyncOnCreate: v })} />
        </label>

        {cloudSyncOn && (
          <div className="border-t border-zinc-100 pt-4" data-testid="ou-cloudsync-wait">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ou.cloudWaitTitle")}</Label>
            <p className="mb-2 text-[11px] text-zinc-400">{t("ou.cloudWaitHelp")}</p>
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={200}
                value={cs.verifyMaxAttempts ?? 20}
                onChange={(e) => setCs({ verifyMaxAttempts: Math.max(1, Math.min(200, Number(e.target.value) || 1)) })}
                data-testid="ou-cloudsync-attempts"
                className="w-24 rounded-sm border border-zinc-300 px-2 py-1.5 font-mono text-sm" />
              <span className="text-xs text-zinc-500">{t("ou.cloudWaitAttempts")}</span>
            </div>
            <label className="mt-3 flex items-center justify-between">
              <div className="pr-6">
                <div className="text-sm font-bold text-zinc-900">{t("ou.cloudBackoff")}</div>
                <div className="text-sm text-zinc-500">{t("ou.cloudBackoffHelp")}</div>
              </div>
              <Switch checked={cs.verifyBackoff !== false} data-testid="ou-cloudsync-backoff"
                onCheckedChange={(v) => setCs({ verifyBackoff: v })} />
            </label>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <div className="pr-6">
            <div className="text-sm font-bold text-zinc-900">{t("ou.deltaTitle")}</div>
            <div className="text-sm text-zinc-500">{t("ou.deltaDesc")}</div>
          </div>
          <Button type="button" onClick={triggerDelta} disabled={syncing || cloudSyncOn} variant="outline" data-testid="ou-trigger-delta" className="rounded-sm border-zinc-300">
            {syncing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}
            {t("ou.deltaBtn")}
          </Button>
        </div>

        {cloudSyncOn && (
          <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700" data-testid="ou-delta-cloudsync-hint">
            {t("ou.deltaCloudSyncDisabled")}
          </p>
        )}
      </div>
    </Section>
  );
}
